// Map-interaction setup: rubber-band route-leg drag + long-press.
//
// Extracted from MapView.tsx as part of the refactor pass. Two
// self-contained pure-DOM-and-MapLibre handlers; the React shell
// in MapView only has to call setupPlanLegRubberBand() and
// setupLongPress() once on map mount.
//
// Each function takes a getCallback() accessor (NOT a callback ref or
// the callback itself) so the React shell can mutate the latest
// callback into a ref and have the handler always read it without
// being torn down and re-attached on every render.

import type {
  Map as MlMap,
  MapMouseEvent,
  GeoJSONSource,
} from 'maplibre-gl';
import maplibregl from 'maplibre-gl';
import { densifyRouteForGreatCircle } from '../lib/geo';
import { usePlanStore } from '../stores/planStore';

const EMPTY_FC = { type: 'FeatureCollection' as const, features: [] };

// Callback types are intentionally local to this module so MapView's
// Props can stay private. MapView's Props happen to use the same
// shape; if they ever diverge, both copies can evolve independently.

export type PlanLegDropCallback = (
  legIndex: number,
  dropLngLat: [number, number],
  screenPoint: { x: number; y: number },
) => void;

export type LongPressCallback = (
  lngLat: [number, number],
  screenPoint: { x: number; y: number },
) => void;

// ---------------------------------------------------------------- //
// Rubber-band route-leg drag                                       //
// ---------------------------------------------------------------- //
//
// ForeFlight-style: tap-and-hold a leg of the active flight plan,
// then drag the cursor (or finger) to a new spot on the map. On
// drop we fire onPlanLegDrop(legIndex, dropLngLat, screenPoint) so
// the parent can open the candidate picker (nearest airport / fix /
// navaid + the raw GPS coord).
//
// Two input paths:
//   1. Desktop: mousedown on 'plan-line' starts the drag.
//   2. Touch: 400ms long-press on 'plan-line' arms drag mode; lifting
//      a finger commits, slop during the hold cancels.
//
// We bail out cleanly if the drag distance is < 6px — that's a
// near-miss the user probably meant as a tap on the line.

export function setupPlanLegRubberBand(
  map: MlMap,
  getCallback: () => PlanLegDropCallback | undefined,
) {
  const RUBBER_SOURCE = 'plan-rubber';
  const RUBBER_LAYER = 'plan-rubber-line';
  const TOUCH_HOLD_MS = 400;
  const TOUCH_SLOP_PX = 6;

  function ensurePreviewLayer() {
    if (!map.getSource(RUBBER_SOURCE)) {
      map.addSource(RUBBER_SOURCE, { type: 'geojson', data: EMPTY_FC });
      map.addLayer({
        id: RUBBER_LAYER,
        type: 'line',
        source: RUBBER_SOURCE,
        paint: {
          'line-color': '#ff5a1f',
          'line-width': 3,
          'line-dasharray': [2, 1.5],
          'line-opacity': 0.85,
        },
      });
    }
  }

  function clearPreview() {
    const src = map.getSource(RUBBER_SOURCE) as GeoJSONSource | undefined;
    if (src) src.setData(EMPTY_FC);
  }

  function paintPreview(
    fromPt: { lat: number; lon: number; ident: string },
    toPt: { lat: number; lon: number; ident: string },
    cursorLngLat: [number, number],
  ) {
    const cursor = { ident: '\u2026', lat: cursorLngLat[1], lon: cursorLngLat[0] };
    const previewCoords = [
      ...densifyRouteForGreatCircle([fromPt, cursor], 25),
      ...densifyRouteForGreatCircle([cursor, toPt], 25).slice(1),
    ];
    const src = map.getSource(RUBBER_SOURCE) as GeoJSONSource | undefined;
    if (src) {
      src.setData({
        type: 'FeatureCollection',
        features: [{
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates: previewCoords },
        }],
      });
    }
  }

  // Visual cue.
  map.on('mouseenter', 'plan-line', () => {
    map.getCanvas().style.cursor = 'grab';
  });
  map.on('mouseleave', 'plan-line', () => {
    map.getCanvas().style.cursor = '';
  });

  // Desktop: mousedown + drag.
  map.on('mousedown', 'plan-line', (e) => {
    const oe = e.originalEvent as MouseEvent | undefined;
    if (oe && (oe as PointerEvent).pointerType && (oe as PointerEvent).pointerType !== 'mouse') return;

    if (!getCallback()) return;
    const feature = e.features?.[0];
    if (!feature) return;
    const legIndex = (feature.properties as { legIndex?: number } | null)?.legIndex;
    if (legIndex == null) return;

    const route = usePlanStore.getState().route;
    const fromPt = route[legIndex];
    const toPt = route[legIndex + 1];
    if (!fromPt || !toPt) return;

    e.preventDefault();
    map.getCanvas().style.cursor = 'grabbing';
    map.dragPan.disable();
    ensurePreviewLayer();

    const startScreen = e.point;
    let didMove = false;

    const onMove = (ev: MapMouseEvent) => {
      const dx = ev.point.x - startScreen.x;
      const dy = ev.point.y - startScreen.y;
      if (!didMove && Math.hypot(dx, dy) < 6) return;
      didMove = true;
      paintPreview(fromPt, toPt, [ev.lngLat.lng, ev.lngLat.lat]);
    };

    const onUp = (ev: MapMouseEvent) => {
      map.off('mousemove', onMove);
      map.off('mouseup', onUp);
      map.dragPan.enable();
      map.getCanvas().style.cursor = '';
      clearPreview();
      if (!didMove) return;
      const cb = getCallback();
      if (!cb) return;
      cb(legIndex, [ev.lngLat.lng, ev.lngLat.lat], { x: ev.point.x, y: ev.point.y });
    };

    map.on('mousemove', onMove);
    map.on('mouseup', onUp);
  });

  // Touch: long-press on plan-line to arm drag.
  let touchTimer: number | null = null;
  let touchArmedLeg:
    | { legIndex: number; from: { ident: string; lat: number; lon: number }; to: { ident: string; lat: number; lon: number } }
    | null = null;
  let touchStart: { x: number; y: number } | null = null;
  let touchArmed = false;
  let touchMoved = false;

  function clearTouchTimer() {
    if (touchTimer != null) {
      window.clearTimeout(touchTimer);
      touchTimer = null;
    }
  }

  function resetTouchState() {
    clearTouchTimer();
    touchArmedLeg = null;
    touchStart = null;
    touchArmed = false;
    touchMoved = false;
    map.getCanvas().style.cursor = '';
    map.dragPan.enable();
    clearPreview();
  }

  const canvas = map.getCanvasContainer();

  canvas.addEventListener('touchstart', (ev) => {
    if (ev.touches.length !== 1) return;
    if (!getCallback()) return;
    const t = ev.touches[0];
    const rect = canvas.getBoundingClientRect();
    const point = { x: t.clientX - rect.left, y: t.clientY - rect.top };
    if (!map.getLayer('plan-line')) return;
    // ⚠️ Mobile fix (2026-05-12): query a 44×44 hit-box around the
    // touch instead of a single pixel. The plan-line is 3px wide in
    // screen space — a finger almost never lands within that band, so
    // the previous single-point query rejected nearly every long-press
    // and the rubber-band feature appeared broken on mobile.
    const HIT = 22; // pixels each side → 44px total touch target
    const fs = map.queryRenderedFeatures(
      [[point.x - HIT, point.y - HIT], [point.x + HIT, point.y + HIT]],
      { layers: ['plan-line'] },
    );
    if (fs.length === 0) return;
    const legIndex = (fs[0].properties as { legIndex?: number } | null)?.legIndex;
    if (legIndex == null) return;
    const route = usePlanStore.getState().route;
    const fromPt = route[legIndex];
    const toPt = route[legIndex + 1];
    if (!fromPt || !toPt) return;

    resetTouchState();
    touchStart = point;
    touchArmedLeg = { legIndex, from: fromPt, to: toPt };
    touchTimer = window.setTimeout(() => {
      touchTimer = null;
      touchArmed = true;
      ensurePreviewLayer();
      map.dragPan.disable();
      const lngLat = map.unproject([point.x, point.y]);
      paintPreview(fromPt, toPt, [lngLat.lng, lngLat.lat]);
      try { (navigator as Navigator & { vibrate?: (ms: number) => void }).vibrate?.(15); } catch { /* noop */ }
    }, TOUCH_HOLD_MS);
  }, { passive: true });

  canvas.addEventListener('touchmove', (ev) => {
    if (!touchStart || !touchArmedLeg) return;
    if (ev.touches.length !== 1) return;
    const t = ev.touches[0];
    const rect = canvas.getBoundingClientRect();
    const point = { x: t.clientX - rect.left, y: t.clientY - rect.top };
    const dx = point.x - touchStart.x;
    const dy = point.y - touchStart.y;
    const moved = Math.hypot(dx, dy) > TOUCH_SLOP_PX;
    if (!touchArmed) {
      if (moved) resetTouchState();
      return;
    }
    touchMoved = true;
    const lngLat = map.unproject([point.x, point.y]);
    paintPreview(touchArmedLeg.from, touchArmedLeg.to, [lngLat.lng, lngLat.lat]);
    if (ev.cancelable) ev.preventDefault();
  }, { passive: false });

  canvas.addEventListener('touchend', (ev) => {
    if (!touchArmedLeg) return;
    const armed = touchArmed;
    const moved = touchMoved;
    const leg = touchArmedLeg;
    let dropPoint = touchStart;
    if (ev.changedTouches.length > 0) {
      const t = ev.changedTouches[0];
      const rect = canvas.getBoundingClientRect();
      dropPoint = { x: t.clientX - rect.left, y: t.clientY - rect.top };
    }
    resetTouchState();
    if (!armed || !moved || !dropPoint) return;
    const lngLat = map.unproject([dropPoint.x, dropPoint.y]);
    const cb = getCallback();
    if (!cb) return;
    cb(leg.legIndex, [lngLat.lng, lngLat.lat], dropPoint);
  }, { passive: true });

  canvas.addEventListener('touchcancel', () => {
    resetTouchState();
  }, { passive: true });
}

// ---------------------------------------------------------------- //
// Long-press                                                       //
// ---------------------------------------------------------------- //
//
// ForeFlight-style: hold for ~500ms anywhere on the map without
// moving more than 6px and we fire onLongPress(lngLat, point).
//
// Returns wasLongPressJustFired() so the click handler can no-op
// when a long-press fired the same gesture (otherwise both menus
// would open back-to-back).

export function setupLongPress(
  map: MlMap,
  getCallback: () => LongPressCallback | undefined,
): { wasLongPressJustFired: () => boolean } {
  const MOUSE_HOLD_MS = 500;
  const TOUCH_HOLD_MS = 450; // slightly shorter — thumb hold reads faster than mouse hold
  const SLOP_PX = 6;
  // Single timer + start-state state machine drives both pointer paths.
  // Whichever pointer (mouse OR touch) starts the hold owns the timer
  // until clear / fire; the other pointer's events are ignored while
  // a hold is armed.
  let timer: number | null = null;
  let startScreen: { x: number; y: number } | null = null;
  let startLngLat: maplibregl.LngLat | null = null;
  let activePointer: 'mouse' | 'touch' | null = null;
  let suppressNextClick = false;

  function clearTimer() {
    if (timer != null) {
      window.clearTimeout(timer);
      timer = null;
    }
    activePointer = null;
  }

  function tryStart(pointer: 'mouse' | 'touch', e: MapMouseEvent | maplibregl.MapTouchEvent) {
    // Don't arm a long-press on top of the active flight-plan line —
    // that gesture belongs to the rubber-band drag handler.
    const planLayers = ['plan-line'].filter((id) => !!map.getLayer(id));
    if (planLayers.length > 0) {
      const fs = map.queryRenderedFeatures(e.point, { layers: planLayers });
      if (fs.length > 0) return;
    }
    startScreen = { x: e.point.x, y: e.point.y };
    startLngLat = e.lngLat;
    suppressNextClick = false;
    activePointer = pointer;
    clearTimer();
    activePointer = pointer;
    const holdMs = pointer === 'touch' ? TOUCH_HOLD_MS : MOUSE_HOLD_MS;
    timer = window.setTimeout(() => {
      timer = null;
      const fired = activePointer;
      activePointer = null;
      suppressNextClick = true;
      // Touch path: also synthesize haptic + tell MapLibre to swallow
      // the click that some browsers fire after touchend, so we don't
      // double-open menus.
      if (fired === 'touch' && typeof navigator !== 'undefined') {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        try { (navigator as any).vibrate?.(15); } catch { /* not available */ }
      }
      const cb = getCallback();
      if (cb && startLngLat && startScreen) {
        cb([startLngLat.lng, startLngLat.lat], { x: startScreen.x, y: startScreen.y });
      }
    }, holdMs);
  }

  function tryMove(e: MapMouseEvent | maplibregl.MapTouchEvent) {
    if (timer == null || !startScreen) return;
    const dx = e.point.x - startScreen.x;
    const dy = e.point.y - startScreen.y;
    if (Math.hypot(dx, dy) > SLOP_PX) clearTimer();
  }

  // ---- Mouse path (desktop) ----
  map.on('mousedown', (e) => tryStart('mouse', e));
  map.on('mousemove', (e) => tryMove(e));
  map.on('mouseup', () => clearTimer());
  map.on('mouseout', () => clearTimer());

  // ---- Touch path (iPad / iPhone) ----
  // MapLibre emits 'touchstart' / 'touchmove' / 'touchend' / 'touchcancel'
  // with the same MapMouseEvent-shaped wrapper. Multi-finger gestures
  // (pinch-zoom, 2-finger ruler) are NOT a long-press — ignore anything
  // beyond one touch point.
  map.on('touchstart', (e) => {
    const pts = (e.originalEvent as TouchEvent | undefined)?.touches;
    if (pts && pts.length > 1) { clearTimer(); return; }
    tryStart('touch', e);
  });
  map.on('touchmove', (e) => {
    const pts = (e.originalEvent as TouchEvent | undefined)?.touches;
    if (pts && pts.length > 1) { clearTimer(); return; }
    tryMove(e);
  });
  map.on('touchend',    () => clearTimer());
  map.on('touchcancel', () => clearTimer());

  // ---- Shared cancels ----
  map.on('dragstart', () => clearTimer());
  map.on('zoomstart', () => clearTimer());

  return {
    wasLongPressJustFired: () => {
      if (suppressNextClick) {
        suppressNextClick = false;
        return true;
      }
      return false;
    },
  };
}
